import React from 'react';
import { Mail, Phone, MapPin, Linkedin, CheckCircle, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { companyInfo, siteContent } from '../data/mockData';

const Footer = () => {
  const navigate = useNavigate();
  const content = siteContent.en;
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavigation = (page, section = null) => {
    if (section) {
      navigate(`/${page}#${section}`);
    } else {
      navigate(`/${page}`);
    }
  };

  const handleLegalNavigation = (legalType) => {
    navigate(`/${legalType}`);
  };

  return (
    <footer className="bg-[#011431] text-white">
      <div className="container mx-auto px-4 lg:px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              <Logo variant="white" className="w-[500px] h-[150px] object-contain" />
            </div>
            <p className="text-gray-300 mb-6 max-w-md leading-relaxed">
              Boutique consulting firm specialized in Quality, Regulatory, and Clinical Affairs 
              for medical devices. Delivering audit-ready excellence across US, EU, and MENA.
            </p>
            <div className="flex items-center space-x-4">
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm">{companyInfo.email}</span>
              </a>
              <a
                href={companyInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => handleNavigation('home')}
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigation('services')}
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigation('about')}
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  About
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigation('insights')}
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Insights
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigation('contact')}
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6">Services</h3>
            <ul className="space-y-3">
              {[
                { name: 'Quality Assurance', onClick: () => handleNavigation('services', 'quality-assurance') },
                { name: 'Global Supplier Audit Support', onClick: () => handleNavigation('services', 'supplier-audit') },
                { name: 'Mock Audits & Inspections', onClick: () => handleNavigation('services', 'mock-audits') },
                { name: 'Gap Assessments', onClick: () => handleNavigation('services', 'gap-assessments') },
                { name: 'Training & Leadership', onClick: () => handleNavigation('services', 'training-leadership') },
                { name: 'Talent Acquisition & Marketplace', onClick: () => handleNavigation('services', 'talent-acquisition') }
              ].map((service) => (
                <li key={service.name}>
                  <button
                    onClick={service.onClick}
                    className="text-gray-300 hover:text-white transition-colors text-sm text-left"
                  >
                    {service.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Standards & Certifications */}
        <div className="border-t border-gray-700 pt-8 mb-8">
          <div className="text-center mb-6">
            <h4 className="text-lg font-bold mb-4">Standards & Expertise</h4>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                'ISO 13485',
                'EU MDR',
                'ISO 14971',
                'ISO 9001'
              ].map((standard) => (
                <div key={standard} className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-[#c41927]" />
                  <span className="text-sm text-gray-300">{standard}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-6 mb-4 md:mb-0">
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4 text-[#c41927]" />
              <span className="text-sm text-gray-300">
                Serving clients across {companyInfo.regions.join(', ')}
              </span>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
            <div className="flex items-center space-x-4 text-xs text-gray-400">
              <button 
                onClick={() => handleLegalNavigation('privacy')}
                className="hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => handleLegalNavigation('terms')}
                className="hover:text-white transition-colors"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => handleLegalNavigation('cookies')}
                className="hover:text-white transition-colors"
              >
                Cookie Policy
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-6 mt-6 text-center">
          <p className="text-sm text-gray-400">
            © {currentYear} {companyInfo.name}. All rights reserved. 
            Quality & Regulatory Excellence for Medical Devices.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;